// Connect to a store-owned endpoint returning Places API (New) Place Details JSON.
// API credentials belong on the server, never in theme settings or browser code.
if (!customElements.get('google-reviews-live')) customElements.define('google-reviews-live', class extends HTMLElement {
  connectedCallback() {
    if (!this.disclosureController) {
      this.disclosureController = new AbortController();
      const disclosure = this.querySelector('.google-reviews__disclosure');
      const viewport = matchMedia('(max-width: 749px)');
      const layout = () => { if (disclosure) disclosure.open = !viewport.matches || this.dataset.mobileLayout === 'cards'; };
      viewport.addEventListener('change', layout, { signal: this.disclosureController.signal });
      layout();
    }
    if (this.controller || this.dataset.source !== 'live' || !this.dataset.endpoint) return;
    try {
      this.endpoint = new URL(this.dataset.endpoint, location.href);
      if (this.endpoint.protocol !== 'https:' || this.endpoint.username || this.endpoint.password) return;
    } catch { return; }
    this.controller = new AbortController();
    this.interval = Math.max(5, Number(this.dataset.interval) || 15) * 60000;
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      if (this.visible) this.refresh(); else clearTimeout(this.timer);
    });
    this.observer.observe(this);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.refresh(); else clearTimeout(this.timer);
    }, { signal: this.controller.signal });
  }
  async refresh() {
    clearTimeout(this.timer);
    if (!this.visible || document.hidden || this.busy || !this.isConnected) return;
    const remaining = this.interval - (Date.now() - (this.lastAttempt || 0));
    if (remaining > 0) { this.timer = setTimeout(() => this.refresh(), remaining); return; }
    // Do not replace cards while someone is reading or interacting with them.
    if (this.contains(document.activeElement) || this.querySelector('[data-dragging]')) {
      this.timer = setTimeout(() => this.refresh(), 30000); return;
    }
    this.busy = true; this.lastAttempt = Date.now();
    this.request = new AbortController();
    const timeout = setTimeout(() => this.request?.abort(), 10000);
    try {
      const response = await fetch(this.endpoint, { signal: this.request.signal, credentials: 'omit', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Reviews unavailable');
      const place = await response.json();
      if (!Number.isFinite(place.rating) || place.rating < 0 || place.rating > 5 || !Number.isInteger(place.userRatingCount) || place.userRatingCount < 0 || !Array.isArray(place.reviews)) throw new Error('Invalid review response');
      if (!this.isConnected) return;
      const track = this.querySelector('[data-rail-track]');
      const cards = place.reviews.map(review => this.card(review)).filter(Boolean);
      track.replaceChildren(...cards);
      track.scrollTo({ left: 0, behavior: 'instant' });
      this.querySelector('[data-review-rating]').textContent = place.rating;
      this.querySelector('[data-review-count]').textContent = place.userRatingCount + ' مراجعة على Google';
      const rating = this.querySelector('.google-reviews__rating'); rating.hidden = false;
      this.stars(place.rating, rating.querySelector('.review-stars'));
      const stamp = this.querySelector('[data-review-updated]'); stamp.hidden = false;
      stamp.textContent = 'آخر تحديث من Google: ' + new Intl.DateTimeFormat(document.documentElement.lang || 'ar', { dateStyle: 'short', timeStyle: 'short' }).format(new Date());
      const providers = this.querySelector('[data-review-providers]'); providers.replaceChildren();
      (Array.isArray(place.attributions) ? place.attributions : []).forEach(p => { if (p.provider) providers.append(this.link(p.providerUri, p.provider)); });
      this.querySelector('[data-review-attribution]').hidden = false;
      this.querySelector('storefront-rail')?.update();
      this.dataset.state = 'updated';
    } catch {
      // Keep the last verified snapshot and its date. Never claim a failed refresh succeeded.
      this.dataset.state = 'unavailable';
    } finally {
      clearTimeout(timeout); this.busy = false;
      if (this.isConnected && this.visible && !document.hidden) this.timer = setTimeout(() => this.refresh(), this.interval);
    }
  }
  node(tag, className, text) { const el = document.createElement(tag); el.className = className; if (text !== undefined) el.textContent = text; return el; }
  safeURL(value) { try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password ? url.href : ''; } catch { return ''; } }
  link(url, text, className = '') {
    const safe = this.safeURL(url), el = this.node(safe ? 'a' : 'span', className, text);
    if (safe) { el.href = safe; el.target = '_blank'; el.rel = 'noopener'; }
    return el;
  }
  stars(rating, el = this.node('span', 'review-stars')) {
    el.setAttribute('role', 'img'); el.setAttribute('aria-label', rating + ' / 5');
    el.style.setProperty('--rating-fill', rating * 20 + '%');
    if (!el.children.length) for (const name of ['base', 'fill']) { const span = this.node('span', 'review-stars__' + name, '★★★★★'); span.setAttribute('aria-hidden', 'true'); el.append(span); }
    return el;
  }
  card(review) {
    const author = review.authorAttribution;
    if (!author?.displayName || !Number.isFinite(review.rating) || review.rating < 1 || review.rating > 5) return null;
    const card = this.node('li', 'google-review'), heading = this.node('div', 'google-review__author');
    const photo = this.safeURL(author.photoUri);
    const avatar = this.node(photo ? 'img' : 'span', 'google-review__avatar', photo ? undefined : author.displayName.slice(0, 1));
    if (photo) { avatar.src = photo; avatar.alt = ''; avatar.loading = 'lazy'; avatar.width = 38; avatar.height = 38; }
    heading.append(avatar);
    const byline = this.node('div', ''); byline.append(this.link(author.uri, author.displayName, 'google-review__name'));
    if (review.relativePublishTimeDescription) byline.append(this.node('span', 'google-review__date', review.relativePublishTimeDescription));
    heading.append(byline); card.append(heading, this.stars(review.rating));
    if (review.text?.text) { const quote = this.node('blockquote', 'google-review__body', review.text.text); quote.tabIndex = 0; card.append(quote); }
    const source = this.safeURL(review.googleMapsUri);
    if (source) card.append(this.link(source, 'عرض على Google', 'google-review__source'));
    return card;
  }
  disconnectedCallback() { this.disclosureController?.abort(); this.disclosureController = null; this.controller?.abort(); this.controller = null; this.request?.abort(); this.observer?.disconnect(); clearTimeout(this.timer); }
});
